<script setup lang="ts">
import { computed, defineProps } from 'vue';

interface Member {
    id: number;
    nom?: string;
    prenom?: string;
    reference?: string;
    photo_url?: string;
    email_verified_at?: string | null;
    actif?: number | boolean;
    collectivite?: { nom?: string } | null;
    [key: string]: any;
}

interface ProgressProps {
    label: string;
    percent: number;
    valueLabel?: string;
}

const props = defineProps<{
    member: Member | null;
    title?: string;
    endDate?: string | null;
    progress?: ProgressProps | null;
}>();

const monthsFr = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const endDateFormatted = computed(() => {
    if (!props.endDate) return '-';
    const date = new Date(props.endDate);
    if (isNaN(date.getTime())) return '-';
    return `${date.getDate()} ${monthsFr[date.getMonth()]} ${date.getFullYear()}`;
});

const clampedPercent = computed(() => {
    if (!props.progress) return 0;
    return Math.max(2, Math.min(100, props.progress.percent || 0));
});
</script>

<template>
    <div class="member-header widget" v-if="member">
        <div class="member-header-avatar">
            <img :src="member.photo_url || '/public/assets/users/300w/user2.jpg'" alt="" />
        </div>
        <div class="member-header-identity">
            <h3 class="mb-1">{{ member.reference || `#${member.id}` }}</h3>
            <p class="text-grey-700 mb-0">{{ member.nom }} {{ member.prenom }}</p>
        </div>
        <div class="member-header-collectivite">
            <p class="text-sm text-grey-500 mb-1">Collectivité</p>
            <p class="font-semibold mb-2">{{ member.collectivite?.nom || 'N/A' }}</p>
            <div class="flex gap-2" style="flex-wrap: wrap;">
                <span v-if="!member.email_verified_at" class="tag-state warning stroke">En cours</span>
                <span v-else class="tag-state success stroke">Vérifié</span>
                <span v-if="member.actif" class="tag-state notif stroke">Actif</span>
                <span v-else class="tag-state danger stroke">Inactif</span>
            </div>
        </div>
        <div class="member-header-date" v-if="endDate">
            <p class="text-sm text-grey-500 mb-1">Date de fin de souscription</p>
            <p class="font-semibold text-xl">{{ endDateFormatted }}</p>
        </div>
        <div class="member-header-progress" v-if="progress">
            <div class="progress-bar">
                <span class="progress-bar-label">{{ progress.label }}</span>
                <div class="progress-bar-fill bg-pop-green" :style="`width: ${clampedPercent}%;`">
                    {{ progress.valueLabel || `${Math.round(progress.percent)}%` }}
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.member-header {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    padding: 1.25rem 1.5rem;
    flex-wrap: wrap;
}

.member-header-avatar {
    flex: 0 0 70px;
    width: 70px;
    height: 70px;
    border-radius: 50%;
    overflow: hidden;
    background-color: rgba(0, 0, 0, 0.04);
}

.member-header-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.member-header-identity {
    min-width: 0;
    flex: 1 1 180px;
}

.member-header-collectivite {
    min-width: 0;
    flex: 1 1 180px;
}

.member-header-collectivite p {
    margin: 0;
}

.member-header-date {
    flex: 1 1 200px;
    min-width: 0;
}

.member-header-date p {
    margin: 0;
}

.member-header-progress {
    flex: 1 1 280px;
    min-width: 200px;
}

.progress-bar {
    position: relative;
    height: 44px;
    border-radius: 100px;
    background-color: rgba(0, 0, 0, 0.08);
    overflow: hidden;
}

.progress-bar-label {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.9rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.6);
    z-index: 1;
    pointer-events: none;
}

.progress-bar-fill {
    height: 100%;
    border-radius: 100px;
    display: flex;
    align-items: center;
    padding: 0 1.25rem;
    font-weight: 600;
    transition: width 0.3s ease;
    white-space: nowrap;
}
</style>
